import { useEffect, useState } from "react";
import { getBlobUrl, getCachedBlobUrl } from "@/offline/blobs";

interface Resolved {
  key: string;
  url: string;
}

function readCached(assetKey: string): Resolved | null {
  const cached = getCachedBlobUrl(assetKey);
  return cached ? { key: assetKey, url: cached } : null;
}

/**
 * Resolves a stable IDB asset key to an `blob:` URL when the blob is cached,
 * otherwise returns the live `fallback` URL. Cached URLs come from a sync
 * module-scoped Map populated by IDB lookups and downloads, so subsequent
 * mounts of the same key render the blob URL on first paint — no flicker.
 *
 * URL lifecycles are owned by the cache (revoked on invalidate / unpin), not
 * by this hook, so multiple components can safely share the same key.
 *
 * Use this hook directly when you need a string URL (e.g. inside a Radix
 * Avatar primitive that doesn't accept a custom image component). Use the
 * `<OfflineImage>` wrapper for plain `<img>` swaps.
 */
export function useOfflineImageUrl(
  assetKey: string,
  fallback: string,
): string {
  const [resolved, setResolved] = useState<Resolved | null>(() =>
    readCached(assetKey),
  );

  useEffect(() => {
    const sync = readCached(assetKey);
    if (sync) {
      if (!resolved || resolved.key !== assetKey || resolved.url !== sync.url) {
        setResolved(sync);
      }
      return;
    }

    let cancelled = false;
    getBlobUrl(assetKey)
      .then((url) => {
        if (cancelled || !url) return;
        setResolved({ key: assetKey, url });
      })
      .catch(() => {
        // IDB lookup failures fall through to the live src — never block.
      });

    return () => {
      cancelled = true;
    };
    // `resolved` intentionally omitted: we re-resolve only when assetKey
    // changes. Including it would cause the effect to re-run on every state
    // update without any new work to do.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetKey]);

  return resolved && resolved.key === assetKey ? resolved.url : fallback;
}

export interface OfflineImageProps
  extends Omit<React.ImgHTMLAttributes<HTMLImageElement>, "src" | "crossOrigin"> {
  /**
   * Content-versioned IDB key, built by the helpers in `asset-keys.ts` (a
   * record thumbnail by image id + content version, a plan image by plan +
   * revision id). Used to look up the cached blob; if missing, falls back to
   * the live `src`.
   */
  assetKey: string;
  /** Live URL — rotating signed S3 URL today. Used online and as fallback. */
  src: string;
  ref?: React.Ref<HTMLImageElement>;
}

/**
 * Drop-in replacement for `<img>` that prefers a cached IDB blob over the
 * live `src` when available. Forwards all other img attributes including ref.
 *
 * Owns the CORS mode of the rendered `<img>` per the S3 image-fetching
 * convention (see `specs/technical/s3-image-fetching.spec.md`): it applies
 * `crossOrigin="anonymous"` when the resolved URL is a remote http(s) S3 URL
 * and omits it for a local `blob:` URL. `crossOrigin` is intentionally absent
 * from the props — callers must not (and cannot) pass it, so no surface can
 * load the same S3 URL in a different (no-CORS) mode and poison the shared
 * cache entry for the CORS consumers.
 */
export function OfflineImage({
  assetKey,
  src,
  ref,
  ...imgProps
}: OfflineImageProps) {
  const resolvedSrc = useOfflineImageUrl(assetKey, src);
  // `blob:` URLs are same-origin; `crossOrigin` on them is a no-op at best and
  // can break the load. Apply anonymous CORS only to remote (S3) URLs. A missing
  // resolved URL (no cached blob and an absent/empty `src`) is treated like a
  // blob — no `crossOrigin` — so this shared primitive degrades to a no-src
  // `<img>` instead of throwing on `.startsWith`. See offline-image.spec.md.
  const corsProps: { crossOrigin?: "anonymous" } =
    !resolvedSrc || resolvedSrc.startsWith("blob:")
      ? {}
      : { crossOrigin: "anonymous" };
  // An empty resolved URL means "no cached blob and no usable live src" — emit
  // `src={undefined}` (omit the attribute) rather than `src=""`. React warns on
  // an empty-string `src` (it triggers a re-download of the whole page), and
  // the spec requires this component to degrade to a plain `<img>` with NO
  // `src`, never crash. See offline-image.spec.md.
  return (
    <img
      ref={ref}
      src={resolvedSrc || undefined}
      {...imgProps}
      {...corsProps}
    />
  );
}
